/** Minimum plausible calendar date; values at/below .NET DateTime.MinValue are ignored */
const MIN_VALID_DATE_MILLISECONDS = new Date("1900-01-02T00:00:00").getTime();

const dateToValidMilliseconds = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const milliseconds = new Date(value).getTime();
  if (
    Number.isNaN(milliseconds) ||
    milliseconds < MIN_VALID_DATE_MILLISECONDS
  ) {
    return null;
  }
  return milliseconds;
};

/** Start of the calendar day after `referenceDate` (local), exclusive lower bound for "later than today". */
const getStartOfTomorrowMilliseconds = (referenceDate = new Date()) => {
  const d = new Date(referenceDate.getTime());
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Parseable `nextSession` instant (any valid calendar date). Null = empty, invalid, or not a date.
 * @param {Object} item - Course / learning activity
 * @returns {number|null}
 */
const getParsableNextSessionMilliseconds = (item) =>
  dateToValidMilliseconds(item?.nextSession);

/**
 * `nextSession` on or after start of tomorrow (local) — used for list sort and for card label.
 * @param {Object} item - Course / learning activity
 * @param {number} minStartMilliseconds - Inclusive; typically start of tomorrow in local time
 * @returns {number|null} Timestamp in milliseconds or null if none
 */
const getNextApplicationSortKey = (item, minStartMilliseconds) => {
  const nextMs = getParsableNextSessionMilliseconds(item);
  if (nextMs != null && nextMs >= minStartMilliseconds) {
    return nextMs;
  }
  return null;
};

/**
 * Timestamp in milliseconds used for next-application ordering for this item — `nextSession`
 * on or after start of tomorrow (local) relative to `referenceDate`. Matches sort comparator logic.
 * @param {Object} item - Course / learning activity
 * @param {Date|string|number} referenceDate - Same instant as `sort(..., { referenceDate })`
 * @returns {number|null}
 */
export const getNextApplicationSortKeyMilliseconds = (item, referenceDate) => {
  const ref =
    referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(ref.getTime())) {
    return null;
  }
  const minStartMilliseconds = getStartOfTomorrowMilliseconds(ref);
  return getNextApplicationSortKey(item, minStartMilliseconds);
};

/**
 * Swedish collation for catalogue titles so Å, Ä, Ö sort after Z (not as A/O variants).
 * Using the runtime default locale breaks this for typical `en-*` browser settings.
 */
const TITLE_COLLATION_LOCALE = "sv";

const titleLocaleCompare = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), TITLE_COLLATION_LOCALE, {
    sensitivity: "base",
  });

/**
 * Title ordering for catalogue lists. Used for `SORT_ORDER_TITLE_ASC_INT` / `title_desc`
 * and for every title tie-break inside `SORT_ORDER_NEXT_APPLICATION_INT` (`nextapplication`).
 */
const compareTitleAscending = (a, b) =>
  titleLocaleCompare(a?.title ?? "", b?.title ?? "");

/** 0 = upcoming nextSession; 1 = parseable date but not upcoming; 2 = not a date */
const getNextApplicationSortTier = (item, minStartMilliseconds) => {
  const pa = getParsableNextSessionMilliseconds(item);
  if (pa == null) {
    return 2;
  }
  const ka = getNextApplicationSortKey(item, minStartMilliseconds);
  if (ka != null) {
    return 0;
  }
  return 1;
};

/**
 * Sort by next application (upcoming session first, then past dates, then undated).
 * Date ties use `nextSession` timestamps; title ties use `compareTitleAscending` (Swedish collation).
 */
const createNextApplicationSorter = (referenceDate) => {
  const minStartMilliseconds = getStartOfTomorrowMilliseconds(referenceDate);
  return (a, b) => {
    const ta = getNextApplicationSortTier(a, minStartMilliseconds);
    const tb = getNextApplicationSortTier(b, minStartMilliseconds);
    if (ta !== tb) {
      return ta - tb;
    }
    const pa = getParsableNextSessionMilliseconds(a);
    const pb = getParsableNextSessionMilliseconds(b);
    const ka = getNextApplicationSortKey(a, minStartMilliseconds);
    const kb = getNextApplicationSortKey(b, minStartMilliseconds);
    if (ta === 0) {
      const byDate = ka - kb;
      if (byDate !== 0) {
        return byDate;
      }
      return compareTitleAscending(a, b);
    }
    if (ta === 1) {
      const byDate = pa - pb;
      if (byDate !== 0) {
        return byDate;
      }
      return compareTitleAscending(a, b);
    }
    return compareTitleAscending(a, b);
  };
};

/**
 * Sorting strategies object containing different sorting methods
 */
const strategies = {
  title: (a, b) => titleLocaleCompare(a?.title ?? "", b?.title ?? ""),
  title_desc: (a, b) => titleLocaleCompare(b?.title ?? "", a?.title ?? ""),
  date: (a, b) => new Date(b.date) - new Date(a.date),
  rating: (a, b) => b.averageRating - a.averageRating,
  news: (a, b) => b.averageRating - a.averageRating,
  type: (a, b) => a.type.localeCompare(b.type),
  default: (a, b) => titleLocaleCompare(a?.title ?? "", b?.title ?? ""),
};

const resolveSorter = (sortBy, options) => {
  const key = sortBy?.toLowerCase();
  if (key === "nextapplication") {
    return createNextApplicationSorter(options?.referenceDate ?? new Date());
  }
  return strategies[key] || strategies.default;
};

/**
 * Sorts an array of items by the specified sort type
 * @param {Array} items - Array of items to sort
 * @param {string} sortBy - Sort type to use (title, date, nextapplication, rating, news, type)
 * @param {Object} [options] - Optional; for nextapplication: `referenceDate` (Date) for tests / fixed "today"
 * @returns {Array} Sorted array of items
 */
const sort = (items, sortBy, options) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const sorter = resolveSorter(sortBy, options);
  return [...items].sort(sorter);
};

/**
 * Gets sorted items using a custom sorter function
 * @param {Array} items - Array of items to sort
 * @param {Function} sorter - Custom sorting function
 * @returns {Array} Sorted array of items
 */
const getSortedItems = (items, sorter) => {
  return Array.isArray(items) ? [...items].sort(sorter) : [];
};

/**
 * Updates a list object by sorting its courses property
 * @param {Object} items - Object containing a courses array
 * @param {string} sortBy - Sort type to use
 * @param {Object} [options] - Optional; for nextapplication: `referenceDate` (Date)
 * @returns {Object} Updated object with sorted courses
 */
const updateList = (items, sortBy, options) => {
  if (!items) {
    return { courses: [] };
  }

  const sorter = resolveSorter(sortBy, options);
  const sortedItems = getSortedItems(items.courses, sorter);

  return {
    ...items,
    courses: sortedItems,
  };
};

/**
 * Sort service object containing all sorting methods
 */
export const sortService = {
  sort,
  updateList,
  getNextApplicationSortKeyMilliseconds,
};
